import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEquipmentColorGroupsAndOrder } from 'store/equipment/equipmentScheduleOrderPageOperations';
import { getEquipment, getWorkers } from 'store/schedule/schedulePageOperation';

/**
 * Loads schedule resources (workers + equipment) for the v6 sidebar.
 *
 * Notes:
 * - We reuse the legacy thunks (`getWorkers`, `getEquipment`) to keep API behaviour identical.
 * - When `showAllLocations` is enabled we request resources for all tenant locations.
 * - The hook is intentionally small so that fetching logic stays out of UI components.
 */
export const useScheduleResourcesLoad = () => {
  const dispatch = useDispatch();
  const showAllLocations = useSelector((state) => state.schedule.showAllLocations);
  const workers = useSelector((state) => state.schedule.resources.workers);
  const equipment = useSelector((state) => state.schedule.resources.equipment);

  const isWorkersLoaded = useMemo(() => Array.isArray(workers), [workers]);
  const isEquipmentLoaded = useMemo(() => Array.isArray(equipment), [equipment]);

  useEffect(() => {
    const options = { allLocations: showAllLocations };

    // We request on first load and when showAllLocations toggles
    // (because the dataset changes).
    dispatch(getWorkers(options));
    dispatch(getEquipment(options));
    dispatch(fetchEquipmentColorGroupsAndOrder(options))
  }, [dispatch, showAllLocations]);

  return {
    isLoaded: isWorkersLoaded && isEquipmentLoaded,
  };
};

export default useScheduleResourcesLoad;

